import type { OrchestratorSchemas } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 스케줄러 상태 조회 · 종착 해제 — `GET /scheduler`(`IFC-RO-59`) · `POST /scheduler/reset`(`IFC-RO-60`).
 *
 * 실물(`openapi.json` · `ctcells-core/app/views/scheduler.py` · 2026-09-29)
 *
 * | 칸 | 조회 | 해제 |
 * | --- | --- | --- |
 * | 본문 | — | `IrreversibleCommandEnvelope{command_id, reason(필수 · 빈 문자열 422)}` |
 * | 헤더 | `X-Control-Token` **선택** — 실리면 `reset_allowed` 가 «이 요청자가 쥐었나»를 본다 | `X-Control-Token` 필수 |
 * | 역할 | operator · admin · viewer | operator · admin |
 * | 성공 | 200 `SchedulerState` | 200 `SchedulerResetResult` — 돌고 있었으면 `reset: false`(자연 멱등) |
 *
 * 🔴 **`state` 는 서버가 읽을 때 계산한다**(`ticking` · `terminated` · `stale` · `never_ran` · `not_started`).
 *    화면은 그 값을 그대로 쓴다. `/readyz` 의 `checks.scheduler` 는 «이 프로세스»의 틱 메모리라
 *    권위가 아니다 — 판정은 이 창구로 한다(v0.9.1).
 * 🔴 **해제는 루프를 깨우지 않는다** — 권위 표만 내리고 다음 주기에 루프가 읽는다. 해제 직후 다시 읽으면
 *    잠깐 `terminated`(메모리 모드) · `stale`(마지막 성공이 옛날)일 수 있다.
 */

export type SchedulerState = OrchestratorSchemas['schemas']['SchedulerState']
export type SchedulerResetResult = OrchestratorSchemas['schemas']['SchedulerResetResult']

/** 조회 한 번 — 제어락 헤더는 전송 계층이 붙인다(`controlToken.ts` 표 · 쥐었을 때만) */
export function readScheduler(): Promise<SchedulerState> {
  return orchestrator.request<SchedulerState>({
    method: 'GET',
    path: '/scheduler',
    retryOn401: true,
  })
}

export interface ResetSchedulerVars {
  /** 🔴 감사 원장에 봉인된다 — 비우지 않는다 */
  reason: string
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

export function resetScheduler({
  reason,
  commandId,
}: ResetSchedulerVars): Promise<SchedulerResetResult> {
  return orchestrator.request<SchedulerResetResult>({
    method: 'POST',
    path: '/scheduler/reset',
    body: { command_id: commandId ?? newCommandId(), reason },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

export const useResetScheduler = createCommand<ResetSchedulerVars, SchedulerResetResult>({
  send: resetScheduler,
  // 스케줄러가 다시 돌면 작업 진행이 바뀐다 — 목록을 다시 받게 한다
  invalidates: () => [keys.batches.all()],
})
